/**
 * `rolesApi` — RTK Query endpoints for the roles service (design §13.2).
 *
 * `GET /roles` answers `{ data: Role[] }` with the full document (incl.
 * `policySpecs[].name/ttlSeconds`): the policy count and TTL in the checklist are
 * derived from that array. Note the service returns **all active roles** unless
 * `?orgId=` is sent, so the checklist is a catalogue, not an org filter.
 *
 * Assign/unassign/renew all take `{ userId }` and are guarded server-side by
 * `roles:assign` on `role:${id}` — the same verdict the UI gates on. Each
 * mutation drops that role's cached verdicts, the org's member verdicts, and
 * invalidates the `Members` tag so the table refreshes.
 */

import { apiSlice } from '@/store/apiSlice';
import { invalidateResource } from '@/features/permissions/can';
import { orgResource, roleResource } from '@/features/permissions/model';
import type { DataEnvelope } from '@/features/orgs/types';
import type { Role } from '../types';

function unwrap<T>(raw: DataEnvelope<T> | T | undefined): T | undefined {
  if (raw && typeof raw === 'object' && 'data' in (raw as DataEnvelope<T>)) {
    return (raw as DataEnvelope<T>).data;
  }
  return raw as T | undefined;
}

/** `POST /roles/:id/{assign,unassign,refresh}` args: `{userId}` body + routing info. */
interface RoleMutationArgs {
  roleId: string;
  userId: string;
  /** Org whose member list should refresh (its `Members` tag), when known. */
  orgId?: string;
}

interface MutationApi {
  queryFulfilled: PromiseLike<unknown>;
}

/** Shared `onQueryStarted`: settle, then re-open the permission question. */
async function trackRoleMutation(
  { roleId, orgId }: RoleMutationArgs,
  { queryFulfilled }: MutationApi,
) {
  try {
    await queryFulfilled;
  } catch {
    // The dialog rolls the row back and toasts; a 403 means the cached verdict
    // was wrong, so it is dropped below either way (P4 — the server wins).
  } finally {
    invalidateResource(roleResource(roleId));
    if (orgId) {
      invalidateResource(orgResource(orgId));
    }
  }
}

// `Role` refreshes the role document, `Members` refreshes the member table whose
// role chips this dialog just changed (per-id tags; see `orgApi` for the how).
const invalidatesTags = (
  _result: unknown,
  _error: unknown,
  { roleId, orgId }: RoleMutationArgs,
) => [
  { type: 'Role' as const, id: roleId },
  ...(orgId ? [{ type: 'Members' as const, id: orgId }] : []),
];

const rolesTaggedApi = apiSlice.enhanceEndpoints({ addTagTypes: ['RoleList', 'Role', 'Members'] });

export const rolesApi = rolesTaggedApi.injectEndpoints({
  endpoints: (builder) => ({
    /** `GET /roles` — the role catalogue for the checklists. */
    getRoles: builder.query<Role[], void>({
      query: () => ({ url: 'roles' }),
      transformResponse: (raw: DataEnvelope<Role[]> | Role[]) => unwrap<Role[]>(raw) ?? [],
      providesTags: [{ type: 'RoleList', id: 'LIST' }],
    }),

    /** `POST /roles/:id/assign` — creates the assignment + mints ephemeral policies. */
    assignRole: builder.mutation<unknown, RoleMutationArgs>({
      query: ({ roleId, userId }) => ({
        url: `roles/${roleId}/assign`,
        method: 'POST',
        body: { userId },
      }),
      invalidatesTags,
      onQueryStarted: trackRoleMutation,
    }),

    /** `POST /roles/:id/unassign` — revokes the assignment. */
    unassignRole: builder.mutation<unknown, RoleMutationArgs>({
      query: ({ roleId, userId }) => ({
        url: `roles/${roleId}/unassign`,
        method: 'POST',
        body: { userId },
      }),
      invalidatesTags,
      onQueryStarted: trackRoleMutation,
    }),

    /** `POST /roles/:id/refresh` — re-materializes the role's ephemeral policies. */
    refreshRole: builder.mutation<unknown, RoleMutationArgs>({
      query: ({ roleId, userId }) => ({
        url: `roles/${roleId}/refresh`,
        method: 'POST',
        body: { userId },
      }),
      invalidatesTags,
      onQueryStarted: trackRoleMutation,
    }),
  }),
});

export const {
  useGetRolesQuery,
  useAssignRoleMutation,
  useUnassignRoleMutation,
  useRefreshRoleMutation,
} = rolesApi;
